<script lang="ts" setup>
import { ref, watch } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import StepDeliveryFormStep from './StepDeliveryFormStep.vue'
import type {
  StepDeliveryFormErrorManager,
  StepDeliveryFormErrorPath,
  StepDeliveryFormErrors,
  StepDeliveryFormType,
} from './formSchema'
import type {
  CreateStepDeliveryInput,
  UpdateStepDeliveryInput,
} from '@/types/schema.generated'
import { validStepDeliveryFormSchema } from './formSchema'
import StepDeliveryFormError from './StepDeliveryFormError.vue'
import * as v from 'valibot'
import DolphinDateInput from '@/components/atoms/DolphinDateInput.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import { parseDateString } from '@/libs/utilities/gql-scalar'

const props = defineProps<{
  initialForm?: StepDeliveryFormType
}>()

const emit = defineEmits<{
  (
    event: 'submit',
    input: UpdateStepDeliveryInput | CreateStepDeliveryInput
  ): void
}>()

const initialForm: StepDeliveryFormType = props.initialForm ?? {
  guid: undefined,
  name: '',
  startAt: null,
  endAt: null,
  cond: '',
  addFriendDate: null,
  IsDisabled: false,
  steps: [
    {
      guid: undefined,
      targetDayAfter: 0,
      targetHour: 0,
      message: '',
      answerSelections: [],
    },
  ],
}

const form = ref<StepDeliveryFormType>({ ...initialForm })

const formErrors = ref<StepDeliveryFormErrors>()

const formErrorManager: StepDeliveryFormErrorManager = {
  get: (path: StepDeliveryFormErrorPath): string | undefined => {
    const errors = formErrors.value
    if (!errors) {
      return undefined
    }

    const messages = errors.nested?.[path]
    if (!messages) {
      return undefined
    }

    return messages[0]
  },
}

const isSubmittedOnce = ref(false)

// 更新内容反映（反映させないと新規追加したstepsが毎回作り直しになる）
watch(
  () => props.initialForm,
  newForm => {
    if (newForm) {
      form.value = { ...newForm }
    }
  },
  { deep: true }
)

watch(form.value, () => {
  const formWithDateStrings = {
    ...form.value,
    guid: form.value.guid,
  }

  if (isSubmittedOnce.value) {
    const result = validate(formWithDateStrings)
    if (!result.success) {
      formErrors.value = result.errors
    }
  }
})

const addStep = () => {
  form.value.steps = [
    ...form.value.steps,
    {
      guid: undefined,
      targetDayAfter: 0,
      targetHour: 0,
      message: '',
      answerSelections: [],
    },
  ]
}

const deleteStep = (index: number) => {
  form.value = {
    ...form.value,
    steps: form.value.steps.filter((_, i) => i !== index),
  }
}

const handleClickSubmitButton = () => {
  isSubmittedOnce.value = true

  if (props.initialForm?.guid != null) {
    form.value.guid = props.initialForm?.guid
  }

  const formWithGuid = {
    ...form.value,
    guid: form.value.guid,
  }

  const result = validate(formWithGuid)

  if (!result.success) {
    formErrors.value = result.errors
    return
  }

  const output = {
    ...result.output,
    addFriendDate: result.output.addFriendDate
      ? parseDateString(result.output.addFriendDate)
      : null,
    startAt: result.output.startAt
      ? parseDateString(result.output.startAt)
      : null,
    endAt: result.output.endAt ? parseDateString(result.output.endAt) : null,
  }

  emit('submit', output)
}

type ValidateResponse =
  | {
      success: true
      output: v.InferOutput<typeof validStepDeliveryFormSchema>
    }
  | {
      success: false
      errors: StepDeliveryFormErrors
    }

const validate = (
  data: StepDeliveryFormType = form.value
): ValidateResponse => {
  const result = v.safeParse(validStepDeliveryFormSchema, data)

  formErrors.value = undefined

  if (result.success) {
    return {
      success: true,
      output: result.output,
    }
  } else {
    return {
      success: false,
      errors: v.flatten(result.issues),
    }
  }
}
</script>

<template>
  <div :class="$style.form">
    <div :class="$style.formRow">
      <p :class="$style.label">ステップ配信名</p>
      <DolphinTextInput v-model="form.name" />
      <StepDeliveryFormError v-if="formErrorManager.get('name')">
        {{ formErrorManager.get('name') }}
      </StepDeliveryFormError>
    </div>
    <div :class="$style.formRow">
      <p :class="$style.label">状態</p>
      <ul :class="($style.options, $style.flex)">
        <li :class="$style.options__item">
          <label :class="[$style.option, $style.center]">
            <DolphinRadioButton
              v-model="form.IsDisabled"
              :value="false"
              name="enabled"
            />
            <span :class="[$style.option__text, $style.margin]">有効</span>
          </label>
        </li>
        <li :class="$style.options__item">
          <label :class="[$style.option, $style.center]">
            <DolphinRadioButton
              v-model="form.IsDisabled"
              :value="true"
              name="enabled"
            />
            <span :class="[$style.option__text, $style.margin]">無効</span>
          </label>
        </li>
      </ul>
      <StepDeliveryFormError v-if="formErrorManager.get('IsDisabled')">
        {{ formErrorManager.get('IsDisabled') }}
      </StepDeliveryFormError>
    </div>

    <div :class="$style.formRow">
      <p :class="$style.label">有効期限</p>
      <p :class="$style.annotation">※空欄の場合は期限無しになります。</p>
      <div :class="$style.flex">
        <div :class="$style.dateWidth">
          <DolphinDateInput
            v-model="form.startAt"
            :maxDate="form.endAt || undefined"
            :format="'yyyy/MM/dd'"
            :clearable="true"
          />
          <StepDeliveryFormError v-if="formErrorManager.get('startAt')">
            {{ formErrorManager.get('startAt') }}
          </StepDeliveryFormError>
        </div>
        <p :class="$style.dateMargin">〜</p>
        <div :class="$style.dateWidth">
          <DolphinDateInput
            v-model="form.endAt"
            :format="'yyyy/MM/dd'"
            :minDate="form.startAt || undefined"
            :clearable="true"
          />
          <StepDeliveryFormError v-if="formErrorManager.get('endAt')">
            {{ formErrorManager.get('endAt') }}
          </StepDeliveryFormError>
        </div>
      </div>
    </div>

    <div :class="$style.formRow">
      <p :class="$style.label">開始条件</p>
      <DolphinSelector
        :class="$style.plannerSelector__select"
        :options="[{ text: '友だち登録', value: 'LineFollow' }]"
        v-model="form.cond"
      />
      <StepDeliveryFormError v-if="formErrorManager.get('cond')">
        {{ formErrorManager.get('cond') }}
      </StepDeliveryFormError>
    </div>

    <!-- 友だち登録が選択された場合のみ友だち追加日を表示 -->
    <div v-if="form.cond === 'LineFollow'" :class="$style.formRow">
      <p :class="$style.label">友だち追加日</p>
      <div :class="$style.dateWidth">
        <DolphinDateInput
          v-model="form.addFriendDate"
          :format="'yyyy/MM/dd'"
          :clearable="true"
        />
        <StepDeliveryFormError v-if="formErrorManager.get('addFriendDate')">
          {{ formErrorManager.get('addFriendDate') }}
        </StepDeliveryFormError>
      </div>
    </div>

    <div>
      <StepDeliveryFormStep
        v-for="(_, i) in form.steps"
        :key="i"
        v-model="form.steps[i]"
        :index="i"
        :shown-control="form.steps.length > 1"
        :form-error-manager="formErrorManager"
        @delete="deleteStep(i)"
      />
      <hr :class="$style.border" />
      <FlatButton
        v-if="form.steps.length < 20"
        @click="addStep"
        variant="secondary"
        >ステップを追加する
      </FlatButton>
      <StepDeliveryFormError v-if="formErrorManager.get('steps')">
        {{ formErrorManager.get('steps') }}
      </StepDeliveryFormError>
    </div>
    <div :class="$style.submitButtonWrapper">
      <FlatButton
        @click="handleClickSubmitButton()"
        variant="primary"
        size="large"
        >保存する
      </FlatButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.formRow {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
}

.flex {
  display: flex;
  align-items: center;
}

.dateWidth {
  width: 40%;
}

.dateMargin {
  margin: 0 20px;
}

.addQuestionButtonWrapper {
  margin-top: 16px;
}

.submitButtonWrapper {
  display: flex;
  justify-content: center;
}

.margin {
  margin: 10px;
}

.border {
  border: 0;
  border-top: 1px solid $color-border-low-emphasis;
  margin-top: 16px;
  padding-top: 16px;
}

.center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.annotation {
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;
}
</style>
